/** Route tree with auth + role guards (PL-03). */
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '../components/AppShell.js';
import { AnalyticsPage } from '../features/analytics/AnalyticsPage.js';
import { BillingPage } from '../features/billing/BillingPage.js';
import { ClassesPage } from '../features/classes/ClassesPage.js';
import { DashboardPage } from '../features/dashboard/DashboardPage.js';
import { MemberDirectory } from '../features/members/index.js';
import { MembershipsPage } from '../features/memberships/MembershipsPage.js';
import { BranchOverview } from '../features/multilocation/BranchOverview.js';
import { RetailPage } from '../features/retail/RetailPage.js';
import { SettingsPage } from '../features/settings/SettingsPage.js';
import { StaffPage } from '../features/staff/StaffPage.js';
import { LoginPage } from '../routes/login.js';
import { Forbidden, NotFound } from '../routes/pages.js';

import { RequireAuth, RequireRole } from './guards.js';

// Match the Vite `base` so deep links work when the app is served from a sub-path
// (e.g. `/admin/` on gymapp.trajanov.com). Falls back to root in dev.
const basename = import.meta.env.BASE_URL.replace(/\/+$/, '') || '/';

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  [
    { path: '/login', element: <LoginPage /> },
    { path: '/403', element: <Forbidden /> },
    {
      element: <RequireAuth />,
      children: [
        {
          element: <AppShell />,
          children: [
            { index: true, element: <Navigate to="/dashboard" replace /> },
            { path: '/dashboard', element: <DashboardPage /> },
            { path: '/classes', element: <ClassesPage /> },
            {
              element: <RequireRole allowed={['owner', 'manager', 'front_desk']} />,
              children: [
                { path: '/members', element: <MemberDirectory /> },
                { path: '/memberships', element: <MembershipsPage /> },
                { path: '/billing', element: <BillingPage /> },
                { path: '/retail', element: <RetailPage /> },
              ],
            },
            {
              element: <RequireRole allowed={['owner', 'manager']} />,
              children: [
                { path: '/staff', element: <StaffPage /> },
                { path: '/analytics', element: <AnalyticsPage /> },
              ],
            },
            {
              element: <RequireRole allowed={['owner']} />,
              children: [
                { path: '/branches', element: <BranchOverview /> },
                { path: '/settings', element: <SettingsPage /> },
              ],
            },
          ],
        },
      ],
    },
    { path: '*', element: <NotFound /> },
  ],
  { basename },
);
